import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/client";
import toast from "react-hot-toast";
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  EnvelopeIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import type { AxiosError } from "axios";

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

interface LocalizedText {
  de: string;
  en: string;
}

type SurveyQuestionType =
  | "single_choice"
  | "multiple_choice"
  | "text"
  | "textarea"
  | "scale"
  | "yes_no"
  | "number"
  | "date"
  | "email";

type SurveyAccessLevel = "public" | "authenticated" | "specific_users" | "user_groups";

interface SurveyQuestion {
  id: string;
  type: SurveyQuestionType;
  question: LocalizedText;
  helpText?: LocalizedText;
  required: boolean;
  options?: LocalizedText[];
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: LocalizedText;
  scaleMaxLabel?: LocalizedText;
  conditionalOn?: {
    questionId: string;
    value: string | string[] | boolean;
  };
  order: number;
}

interface Survey {
  _id?: string;
  slug: string;
  title: LocalizedText;
  description?: LocalizedText;
  questions: SurveyQuestion[];
  accessLevel: SurveyAccessLevel;
  allowedUserIds?: string[];
  allowedGroups?: string[];
  isActive: boolean;
  isListed: boolean;
  allowAnonymous: boolean;
  allowMultipleResponses: boolean;
  showProgressBar: boolean;
  showQuestionNumbers: boolean;
  thankYouMessage?: LocalizedText;
  redirectUrl?: string;
  expiresAt?: string;
  maxResponses?: number;
  responseCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface SurveyResponse {
  _id: string;
  surveyId: string;
  userId?: string;
  email?: string;
  answers: Array<{
    questionId: string;
    value: unknown;
  }>;
  completedAt: string;
  timeSpentSeconds?: number;
}

interface UserGroup {
  _id: string;
  name: string;
  description?: string;
  memberCount?: number;
}

interface SurveyAnalytics {
  totalResponses: number;
  completionRate: number;
  avgTimeSpent: number;
  questionStats: Record<string, {
    questionText: string;
    type: string;
    totalAnswered: number;
    optionCounts?: Record<string, number>;
    average?: number;
    distribution?: Record<number, number>;
    sampleResponses?: string[];
  }>;
  responseTimeline: Array<{ date: string; count: number }>;
  survey: Survey;
}

interface InvitationFilter {
  status?: string;
  userGroupIds?: string[];
  completedMilestone?: string;
  notCompletedMilestone?: string;
  tags?: string[];
}

// User stage presets for common filtering scenarios
const userStagePresets = [
  { label: "Registriert, keine Dokumente", completed: "accountCreated", notCompleted: "documentsUploaded" },
  { label: "Dokumente hochgeladen, kein DocuSign", completed: "documentsUploaded", notCompleted: "contractCompleted" },
  { label: "DocuSign abgeschlossen, keine Auszahlung", completed: "contractCompleted", notCompleted: "payoutExecuted" },
  { label: "Auszahlung erhalten", completed: "payoutExecuted", notCompleted: undefined },
];

const questionTypeLabels: Record<SurveyQuestionType, string> = {
  single_choice: "Single Choice",
  multiple_choice: "Multiple Choice",
  text: "Short Text",
  textarea: "Long Text",
  scale: "Scale/Rating",
  yes_no: "Yes/No",
  number: "Number",
  date: "Date",
  email: "Email",
};

const accessLevelLabels: Record<SurveyAccessLevel, string> = {
  public: "Public (Anyone)",
  authenticated: "Authenticated Users",
  specific_users: "Specific Users",
  user_groups: "User Groups",
};

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

function createEmptyQuestion(order: number): SurveyQuestion {
  return {
    id: generateId(),
    type: "text",
    question: { de: "", en: "" },
    required: false,
    order,
  };
}

export default function Surveys() {
  const queryClient = useQueryClient();
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [viewingResponses, setViewingResponses] = useState<string | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [viewingAnalytics, setViewingAnalytics] = useState<string | null>(null);
  const [showCampaignModal, setShowCampaignModal] = useState<string | null>(null);
  const [campaignFilter, setCampaignFilter] = useState<InvitationFilter>({});
  const [campaignMessage, setCampaignMessage] = useState("");
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isSendingInvitations, setIsSendingInvitations] = useState(false);

  // Fetch surveys
  const { data: surveys, isLoading } = useQuery({
    queryKey: ["surveys"],
    queryFn: async () => {
      const res = await api.get("/admin/surveys");
      return res.data.data as Survey[];
    },
  });

  // Fetch user groups for access control
  const { data: userGroups } = useQuery({
    queryKey: ["user-groups"],
    queryFn: async () => {
      const res = await api.get("/admin/user-groups");
      return res.data.data as UserGroup[];
    },
  });

  // Fetch responses for a survey
  const { data: responsesData } = useQuery({
    queryKey: ["survey-responses", viewingResponses],
    queryFn: async () => {
      if (!viewingResponses) return null;
      const res = await api.get(`/admin/surveys/${viewingResponses}/responses`);
      return res.data.data as { responses: SurveyResponse[]; totalCount: number; survey: Survey };
    },
    enabled: !!viewingResponses,
  });
  const responses = responsesData?.responses;

  // Fetch analytics for a survey
  const { data: analyticsData, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ["survey-analytics", viewingAnalytics],
    queryFn: async () => {
      if (!viewingAnalytics) return null;
      const res = await api.get(`/admin/surveys/${viewingAnalytics}/analytics`);
      return res.data.data as SurveyAnalytics;
    },
    enabled: !!viewingAnalytics,
  });

  // Create survey mutation
  const createMutation = useMutation({
    mutationFn: async (survey: Omit<Survey, "_id">) => {
      const res = await api.post("/admin/surveys", survey);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      toast.success("Survey created");
      setShowCreateForm(false);
      setEditingSurvey(null);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || "Failed to create survey");
    },
  });

  // Update survey mutation
  const updateMutation = useMutation({
    mutationFn: async (survey: Survey) => {
      const res = await api.put(`/admin/surveys/${survey._id}`, survey);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      toast.success("Survey updated");
      setEditingSurvey(null);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || "Failed to update survey");
    },
  });

  // Delete survey mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/surveys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      toast.success("Survey deleted");
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || "Failed to delete survey");
    },
  });

  const handleCreateNew = () => {
    setEditingSurvey({
      slug: "",
      title: { de: "", en: "" },
      description: { de: "", en: "" },
      questions: [createEmptyQuestion(0)],
      accessLevel: "public",
      isActive: true,
      isListed: false,
      allowAnonymous: true,
      allowMultipleResponses: false,
      showProgressBar: true,
      showQuestionNumbers: true,
      thankYouMessage: { de: "Vielen Dank!", en: "Thank you!" },
    });
    setShowCreateForm(true);
  };

  const handleSave = () => {
    if (!editingSurvey) return;
    if (!editingSurvey.slug.trim()) {
      toast.error("Slug is required");
      return;
    }
    if (!editingSurvey.title.de.trim() || !editingSurvey.title.en.trim()) {
      toast.error("Title is required in both languages");
      return;
    }

    if (editingSurvey._id) {
      updateMutation.mutate(editingSurvey);
    } else {
      createMutation.mutate(editingSurvey);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this survey? All responses will also be deleted.")) {
      deleteMutation.mutate(id);
    }
  };

  const handleDuplicate = (survey: Survey) => {
    setEditingSurvey({
      ...survey,
      _id: undefined,
      slug: `${survey.slug}-copy`,
      title: {
        de: `${survey.title.de} (Kopie)`,
        en: `${survey.title.en} (Copy)`,
      },
      responseCount: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    });
    setShowCreateForm(true);
  };

  // CSV Export
  const exportToCSV = (survey: Survey, responses: SurveyResponse[]) => {
    if (!responses || responses.length === 0) {
      toast.error("No responses to export");
      return;
    }

    // Build headers: Date, User/Email, Time Spent, then each question
    const headers = ["Date", "User/Email", "Time Spent (seconds)"];
    for (const q of survey.questions) {
      headers.push(q.question.de || q.question.en || q.id);
    }

    // Build rows
    const rows: string[][] = [];
    for (const response of responses) {
      const row: string[] = [
        new Date(response.completedAt).toISOString(),
        response.email || response.userId || "Anonymous",
        String(response.timeSpentSeconds || ""),
      ];

      for (const q of survey.questions) {
        const answer = response.answers.find((a) => a.questionId === q.id);
        if (answer) {
          const val = answer.value;
          if (Array.isArray(val)) {
            row.push(val.join("; "));
          } else if (typeof val === "object") {
            row.push(JSON.stringify(val));
          } else {
            row.push(String(val ?? ""));
          }
        } else {
          row.push("");
        }
      }
      rows.push(row);
    }

    // Convert to CSV string
    const escapeCsv = (val: string) => {
      if (val.includes(",") || val.includes('"') || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((r) => r.map(escapeCsv).join(",")),
    ].join("\n");

    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `survey-${survey.slug}-responses.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("CSV exported successfully");
  };

  // Campaign preview
  const handlePreviewCount = async () => {
    if (!showCampaignModal) return;
    try {
      const res = await api.post(`/admin/surveys/${showCampaignModal}/send-invitations`, {
        filter: campaignFilter,
        previewOnly: true,
      });
      setPreviewCount(res.data.data.recipientCount);
    } catch (error: any) {
      toast.error("Failed to get preview count");
    }
  };

  // Send invitations
  const handleSendInvitations = async () => {
    if (!showCampaignModal) return;
    if (previewCount === 0) {
      toast.error("No recipients match the filter");
      return;
    }

    setIsSendingInvitations(true);
    try {
      const res = await api.post(`/admin/surveys/${showCampaignModal}/send-invitations`, {
        filter: campaignFilter,
        customMessage: campaignMessage || undefined,
        previewOnly: false,
      });
      const { sentCount, recipientCount, errors } = res.data.data;
      if (errors && errors.length > 0) {
        toast.error(`Sent ${sentCount}/${recipientCount} invitations. Some failed.`);
      } else {
        toast.success(`Successfully sent ${sentCount} invitation(s)`);
      }
      setShowCampaignModal(null);
      setCampaignFilter({});
      setCampaignMessage("");
      setPreviewCount(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to send invitations");
    } finally {
      setIsSendingInvitations(false);
    }
  };

  const addQuestion = () => {
    if (!editingSurvey) return;
    const newQuestion = createEmptyQuestion(editingSurvey.questions.length);
    setEditingSurvey({
      ...editingSurvey,
      questions: [...editingSurvey.questions, newQuestion],
    });
    setExpandedQuestion(newQuestion.id);
  };

  const updateQuestion = (questionId: string, updates: Partial<SurveyQuestion>) => {
    if (!editingSurvey) return;
    setEditingSurvey({
      ...editingSurvey,
      questions: editingSurvey.questions.map((q) =>
        q.id === questionId ? { ...q, ...updates } : q
      ),
    });
  };

  const removeQuestion = (questionId: string) => {
    if (!editingSurvey) return;
    setEditingSurvey({
      ...editingSurvey,
      questions: editingSurvey.questions
        .filter((q) => q.id !== questionId)
        .map((q, i) => ({ ...q, order: i })),
    });
  };

  const moveQuestion = (questionId: string, direction: "up" | "down") => {
    if (!editingSurvey) return;
    const idx = editingSurvey.questions.findIndex((q) => q.id === questionId);
    if (
      (direction === "up" && idx === 0) ||
      (direction === "down" && idx === editingSurvey.questions.length - 1)
    ) {
      return;
    }

    const newQuestions = [...editingSurvey.questions];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    [newQuestions[idx], newQuestions[swapIdx]] = [newQuestions[swapIdx], newQuestions[idx]];
    newQuestions.forEach((q, i) => (q.order = i));

    setEditingSurvey({
      ...editingSurvey,
      questions: newQuestions,
    });
  };

  const addOption = (questionId: string) => {
    if (!editingSurvey) return;
    const question = editingSurvey.questions.find((q) => q.id === questionId);
    if (!question) return;

    updateQuestion(questionId, {
      options: [...(question.options || []), { de: "", en: "" }],
    });
  };

  const updateOption = (questionId: string, optionIdx: number, value: LocalizedText) => {
    if (!editingSurvey) return;
    const question = editingSurvey.questions.find((q) => q.id === questionId);
    if (!question?.options) return;

    const newOptions = [...question.options];
    newOptions[optionIdx] = value;
    updateQuestion(questionId, { options: newOptions });
  };

  const removeOption = (questionId: string, optionIdx: number) => {
    if (!editingSurvey) return;
    const question = editingSurvey.questions.find((q) => q.id === questionId);
    if (!question?.options) return;

    updateQuestion(questionId, {
      options: question.options.filter((_, i) => i !== optionIdx),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading surveys...</div>
      </div>
    );
  }

  // Survey Editor Form
  if (showCreateForm && editingSurvey) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ClipboardDocumentListIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {editingSurvey._id ? "Edit Survey" : "Create Survey"}
            </h1>
          </div>
          <button
            onClick={() => {
              setShowCreateForm(false);
              setEditingSurvey(null);
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slug (URL identifier) *
              </label>
              <input
                type="text"
                value={editingSurvey.slug}
                onChange={(e) =>
                  setEditingSurvey({ ...editingSurvey, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
                }
                placeholder="my-survey"
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500">URL: /survey/{editingSurvey.slug || "..."}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Access Level
              </label>
              <select
                value={editingSurvey.accessLevel}
                onChange={(e) =>
                  setEditingSurvey({ ...editingSurvey, accessLevel: e.target.value as SurveyAccessLevel })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {Object.entries(accessLevelLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Title */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title (German) *
              </label>
              <input
                type="text"
                value={editingSurvey.title.de}
                onChange={(e) =>
                  setEditingSurvey({ ...editingSurvey, title: { ...editingSurvey.title, de: e.target.value } })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title (English) *
              </label>
              <input
                type="text"
                value={editingSurvey.title.en}
                onChange={(e) =>
                  setEditingSurvey({ ...editingSurvey, title: { ...editingSurvey.title, en: e.target.value } })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (German)
              </label>
              <textarea
                value={editingSurvey.description?.de || ""}
                onChange={(e) =>
                  setEditingSurvey({
                    ...editingSurvey,
                    description: { ...editingSurvey.description, de: e.target.value, en: editingSurvey.description?.en || "" },
                  })
                }
                rows={3}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (English)
              </label>
              <textarea
                value={editingSurvey.description?.en || ""}
                onChange={(e) =>
                  setEditingSurvey({
                    ...editingSurvey,
                    description: { de: editingSurvey.description?.de || "", en: e.target.value },
                  })
                }
                rows={3}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* User Groups (if access level is user_groups) */}
          {editingSurvey.accessLevel === "user_groups" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Allowed Groups
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {userGroups?.map((group) => (
                  <label key={group._id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingSurvey.allowedGroups?.includes(group._id) || false}
                      onChange={(e) => {
                        const groups = editingSurvey.allowedGroups || [];
                        setEditingSurvey({
                          ...editingSurvey,
                          allowedGroups: e.target.checked
                            ? [...groups, group._id]
                            : groups.filter((g) => g !== group._id),
                        });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{group.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Settings Toggles */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingSurvey.isActive}
                onChange={(e) => setEditingSurvey({ ...editingSurvey, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingSurvey.isListed}
                onChange={(e) => setEditingSurvey({ ...editingSurvey, isListed: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Listed publicly</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingSurvey.allowAnonymous}
                onChange={(e) => setEditingSurvey({ ...editingSurvey, allowAnonymous: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Allow anonymous</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingSurvey.allowMultipleResponses}
                onChange={(e) => setEditingSurvey({ ...editingSurvey, allowMultipleResponses: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Multiple responses</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingSurvey.showProgressBar}
                onChange={(e) => setEditingSurvey({ ...editingSurvey, showProgressBar: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Show progress bar</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingSurvey.showQuestionNumbers}
                onChange={(e) => setEditingSurvey({ ...editingSurvey, showQuestionNumbers: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Show question numbers</span>
            </label>
          </div>

          {/* Optional Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expires At (optional)
              </label>
              <input
                type="datetime-local"
                value={editingSurvey.expiresAt ? new Date(editingSurvey.expiresAt).toISOString().slice(0, 16) : ""}
                onChange={(e) =>
                  setEditingSurvey({ ...editingSurvey, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Responses (optional)
              </label>
              <input
                type="number"
                value={editingSurvey.maxResponses || ""}
                onChange={(e) =>
                  setEditingSurvey({ ...editingSurvey, maxResponses: e.target.value ? parseInt(e.target.value) : undefined })
                }
                min={1}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Thank You Message */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Thank You Message (German)
              </label>
              <input
                type="text"
                value={editingSurvey.thankYouMessage?.de || ""}
                onChange={(e) =>
                  setEditingSurvey({
                    ...editingSurvey,
                    thankYouMessage: { de: e.target.value, en: editingSurvey.thankYouMessage?.en || "" },
                  })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Thank You Message (English)
              </label>
              <input
                type="text"
                value={editingSurvey.thankYouMessage?.en || ""}
                onChange={(e) =>
                  setEditingSurvey({
                    ...editingSurvey,
                    thankYouMessage: { de: editingSurvey.thankYouMessage?.de || "", en: e.target.value },
                  })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Questions */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Questions ({editingSurvey.questions.length})
              </h3>
              <button
                onClick={addQuestion}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Question
              </button>
            </div>

            <div className="space-y-4">
              {editingSurvey.questions.map((question, qIdx) => (
                <div
                  key={question.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  {/* Question Header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer"
                    onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">#{qIdx + 1}</span>
                      <span className="text-sm text-gray-900 dark:text-white truncate max-w-md">
                        {question.question.de || question.question.en || "(No question text)"}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                        {questionTypeLabels[question.type]}
                      </span>
                      {question.required && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                          Required
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveQuestion(question.id, "up");
                        }}
                        disabled={qIdx === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveQuestion(question.id, "down");
                        }}
                        disabled={qIdx === editingSurvey.questions.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeQuestion(question.id);
                        }}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Question Editor (Expanded) */}
                  {expandedQuestion === question.id && (
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Question Type
                          </label>
                          <select
                            value={question.type}
                            onChange={(e) => updateQuestion(question.id, { type: e.target.value as SurveyQuestionType })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            {Object.entries(questionTypeLabels).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={question.required}
                              onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                          </label>
                        </div>
                      </div>

                      {/* Question Text */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Question (German) *
                          </label>
                          <input
                            type="text"
                            value={question.question.de}
                            onChange={(e) =>
                              updateQuestion(question.id, { question: { ...question.question, de: e.target.value } })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Question (English) *
                          </label>
                          <input
                            type="text"
                            value={question.question.en}
                            onChange={(e) =>
                              updateQuestion(question.id, { question: { ...question.question, en: e.target.value } })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      {/* Help Text */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Help Text (German)
                          </label>
                          <input
                            type="text"
                            value={question.helpText?.de || ""}
                            onChange={(e) =>
                              updateQuestion(question.id, {
                                helpText: { de: e.target.value, en: question.helpText?.en || "" },
                              })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Help Text (English)
                          </label>
                          <input
                            type="text"
                            value={question.helpText?.en || ""}
                            onChange={(e) =>
                              updateQuestion(question.id, {
                                helpText: { de: question.helpText?.de || "", en: e.target.value },
                              })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      {/* Options (for choice questions) */}
                      {(question.type === "single_choice" || question.type === "multiple_choice") && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Options
                            </label>
                            <button
                              onClick={() => addOption(question.id)}
                              className="text-sm text-indigo-600 hover:text-indigo-800"
                            >
                              + Add Option
                            </button>
                          </div>
                          <div className="space-y-2">
                            {(question.options || []).map((option, optIdx) => (
                              <div key={optIdx} className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={option.de}
                                  onChange={(e) =>
                                    updateOption(question.id, optIdx, { ...option, de: e.target.value })
                                  }
                                  placeholder="German"
                                  className="flex-1 text-sm rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <input
                                  type="text"
                                  value={option.en}
                                  onChange={(e) =>
                                    updateOption(question.id, optIdx, { ...option, en: e.target.value })
                                  }
                                  placeholder="English"
                                  className="flex-1 text-sm rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <button
                                  onClick={() => removeOption(question.id, optIdx)}
                                  className="p-1 text-red-400 hover:text-red-600"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Scale settings */}
                      {question.type === "scale" && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Min Value
                            </label>
                            <input
                              type="number"
                              value={question.scaleMin ?? 1}
                              onChange={(e) => updateQuestion(question.id, { scaleMin: parseInt(e.target.value) })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Max Value
                            </label>
                            <input
                              type="number"
                              value={question.scaleMax ?? 5}
                              onChange={(e) => updateQuestion(question.id, { scaleMax: parseInt(e.target.value) })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Min Label (DE/EN)
                            </label>
                            <input
                              type="text"
                              value={question.scaleMinLabel?.de || ""}
                              onChange={(e) =>
                                updateQuestion(question.id, {
                                  scaleMinLabel: { de: e.target.value, en: question.scaleMinLabel?.en || "" },
                                })
                              }
                              placeholder="e.g., Schlecht"
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Max Label (DE/EN)
                            </label>
                            <input
                              type="text"
                              value={question.scaleMaxLabel?.de || ""}
                              onChange={(e) =>
                                updateQuestion(question.id, {
                                  scaleMaxLabel: { de: e.target.value, en: question.scaleMaxLabel?.en || "" },
                                })
                              }
                              placeholder="e.g., Sehr gut"
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingSurvey(null);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {createMutation.isPending || updateMutation.isPending ? "Saving..." : "Save Survey"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Analytics View
  if (viewingAnalytics && analyticsData) {
    const survey = analyticsData.survey;
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ChartBarIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Analytics: {survey?.title.de || survey?.title.en}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (survey && responses) {
                  exportToCSV(survey, responses);
                } else {
                  // Fetch responses first for export
                  api.get(`/admin/surveys/${viewingAnalytics}/responses`).then((res) => {
                    if (survey) {
                      exportToCSV(survey, res.data.data.responses);
                    }
                  });
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => setViewingAnalytics(null)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Responses</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {analyticsData.totalResponses}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg. Time Spent</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {analyticsData.avgTimeSpent > 0
                ? `${Math.floor(analyticsData.avgTimeSpent / 60)}m ${Math.round(analyticsData.avgTimeSpent % 60)}s`
                : "-"}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Questions</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {survey?.questions.length || 0}
            </div>
          </div>
        </div>

        {/* Response Timeline (simple bar chart) */}
        {analyticsData.responseTimeline.some((d) => d.count > 0) && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Responses (Last 30 Days)
            </h3>
            <div className="flex items-end space-x-1 h-32">
              {analyticsData.responseTimeline.map((d, i) => {
                const maxCount = Math.max(...analyticsData.responseTimeline.map((x) => x.count), 1);
                const height = (d.count / maxCount) * 100;
                return (
                  <div
                    key={i}
                    className="flex-1 bg-indigo-500 dark:bg-indigo-600 rounded-t hover:bg-indigo-600 dark:hover:bg-indigo-500 transition-colors"
                    style={{ height: `${Math.max(height, d.count > 0 ? 4 : 0)}%` }}
                    title={`${d.date}: ${d.count} response(s)`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
              <span>{analyticsData.responseTimeline[0]?.date}</span>
              <span>{analyticsData.responseTimeline[analyticsData.responseTimeline.length - 1]?.date}</span>
            </div>
          </div>
        )}

        {/* Per-Question Stats */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Question Statistics
          </h3>
          <div className="space-y-6">
            {survey?.questions.map((q, qIdx) => {
              const stats = analyticsData.questionStats[q.id];
              if (!stats) return null;

              return (
                <div key={q.id} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        #{qIdx + 1} ({questionTypeLabels[q.type]})
                      </span>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {stats.questionText}
                      </h4>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {stats.totalAnswered} answer(s)
                    </span>
                  </div>

                  {/* Choice questions: bar chart */}
                  {stats.optionCounts && Object.keys(stats.optionCounts).length > 0 && (
                    <div className="space-y-2">
                      {Object.entries(stats.optionCounts).map(([option, count]) => {
                        const total = stats.totalAnswered || 1;
                        const percentage = Math.round((count / total) * 100);
                        return (
                          <div key={option} className="flex items-center gap-3">
                            <span className="w-32 text-sm text-gray-700 dark:text-gray-300 truncate" title={option}>
                              {option === "yes" ? "Ja" : option === "no" ? "Nein" : option}
                            </span>
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded h-6 overflow-hidden">
                              <div
                                className="bg-indigo-500 h-6 rounded transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="w-20 text-sm text-right text-gray-600 dark:text-gray-400">
                              {count} ({percentage}%)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Scale/number questions: average and distribution */}
                  {stats.average !== undefined && (
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {stats.average.toFixed(1)}
                        <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                          average
                        </span>
                      </div>
                      {stats.distribution && (
                        <div className="flex items-end gap-1 h-16">
                          {Object.entries(stats.distribution)
                            .sort(([a], [b]) => Number(a) - Number(b))
                            .map(([value, count]) => {
                              const maxDist = Math.max(...Object.values(stats.distribution!), 1);
                              const height = (count / maxDist) * 100;
                              return (
                                <div key={value} className="flex flex-col items-center flex-1">
                                  <div
                                    className="w-full bg-indigo-500 dark:bg-indigo-600 rounded-t"
                                    style={{ height: `${Math.max(height, 4)}%` }}
                                  />
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{value}</span>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Text questions: sample responses */}
                  {stats.sampleResponses && stats.sampleResponses.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Sample responses:</span>
                      <ul className="list-disc list-inside space-y-1">
                        {stats.sampleResponses.map((text, i) => (
                          <li key={i} className="text-sm text-gray-700 dark:text-gray-300 truncate">
                            {text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Loading analytics
  if (viewingAnalytics && isLoadingAnalytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  // Response Viewer
  if (viewingResponses) {
    const survey = responsesData?.survey || surveys?.find((s) => s._id === viewingResponses);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <EyeIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Responses: {survey?.title.de || survey?.title.en}
            </h1>
          </div>
          <button
            onClick={() => setViewingResponses(null)}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {!responses || responses.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No responses yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User/Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Time Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Answers
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {responses.map((response) => (
                    <tr key={response._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(response.completedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {response.email || response.userId || "Anonymous"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {response.timeSpentSeconds
                          ? `${Math.floor(response.timeSpentSeconds / 60)}m ${response.timeSpentSeconds % 60}s`
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        <details>
                          <summary className="cursor-pointer text-indigo-600 hover:text-indigo-800">
                            View answers ({response.answers.length})
                          </summary>
                          <div className="mt-2 space-y-2">
                            {response.answers.map((answer, i) => {
                              const question = survey?.questions.find((q) => q.id === answer.questionId);
                              return (
                                <div key={i} className="text-xs">
                                  <span className="font-medium">{question?.question.de || answer.questionId}:</span>{" "}
                                  {JSON.stringify(answer.value)}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Survey List
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ClipboardDocumentListIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Surveys</h1>
        </div>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Survey
        </button>
      </div>

      {!surveys || surveys.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
          <ClipboardDocumentListIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No surveys yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Create your first survey to start collecting feedback.
          </p>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Survey
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Survey
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Responses
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {surveys.map((survey) => (
                <tr key={survey._id}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {survey.title.de || survey.title.en}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      /survey/{survey.slug}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          survey.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {survey.isActive ? "Active" : "Inactive"}
                      </span>
                      {survey.isListed && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          Listed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {accessLevelLabels[survey.accessLevel]}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {survey.responseCount || 0}
                    {survey.maxResponses && (
                      <span className="text-gray-500 dark:text-gray-400"> / {survey.maxResponses}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setViewingAnalytics(survey._id!)}
                        className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                        title="Analytics"
                      >
                        <ChartBarIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setViewingResponses(survey._id!)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="View Responses"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setShowCampaignModal(survey._id!);
                          setPreviewCount(null);
                          setCampaignFilter({});
                          setCampaignMessage("");
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Send Invitations"
                      >
                        <EnvelopeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingSurvey(survey);
                          setShowCreateForm(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(survey)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(survey._id!)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Survey URLs</h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              <p>Surveys are accessible at:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>
                  <code>/:locale/survey/:slug</code> - Public survey page
                </li>
              </ul>
              <p className="mt-2">
                Unlisted surveys are only accessible via direct link. Listed surveys may appear in navigation
                (if implemented).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowCampaignModal(null)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Send Survey Invitations
                  </h3>
                  <button
                    onClick={() => setShowCampaignModal(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Survey: {surveys?.find((s) => s._id === showCampaignModal)?.title.de}
                </p>
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Filter: Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    User Status
                  </label>
                  <select
                    value={campaignFilter.status || ""}
                    onChange={(e) => {
                      setCampaignFilter({ ...campaignFilter, status: e.target.value || undefined });
                      setPreviewCount(null);
                    }}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">All Users</option>
                    <option value="verified">Verified Only</option>
                    <option value="pending">Pending Only</option>
                  </select>
                </div>

                {/* Filter: User Groups */}
                {userGroups && userGroups.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      User Groups
                    </label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                      {userGroups.map((group) => (
                        <label key={group._id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={campaignFilter.userGroupIds?.includes(group._id) || false}
                            onChange={(e) => {
                              const groups = campaignFilter.userGroupIds || [];
                              setCampaignFilter({
                                ...campaignFilter,
                                userGroupIds: e.target.checked
                                  ? [...groups, group._id]
                                  : groups.filter((g) => g !== group._id),
                              });
                              setPreviewCount(null);
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{group.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* User Stage Presets */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quick Filter: User Stage
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {userStagePresets.map((preset) => {
                      const isActive =
                        campaignFilter.completedMilestone === preset.completed &&
                        campaignFilter.notCompletedMilestone === preset.notCompleted;
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            if (isActive) {
                              setCampaignFilter({
                                ...campaignFilter,
                                completedMilestone: undefined,
                                notCompletedMilestone: undefined
                              });
                            } else {
                              setCampaignFilter({
                                ...campaignFilter,
                                completedMilestone: preset.completed,
                                notCompletedMilestone: preset.notCompleted
                              });
                            }
                            setPreviewCount(null);
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            isActive
                              ? "bg-indigo-600 text-white"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                          }`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Filter: Milestone (Advanced) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Has Completed
                    </label>
                    <select
                      value={campaignFilter.completedMilestone || ""}
                      onChange={(e) => {
                        setCampaignFilter({ ...campaignFilter, completedMilestone: e.target.value || undefined });
                        setPreviewCount(null);
                      }}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Any</option>
                      <option value="accountCreated">Account Created</option>
                      <option value="emailConfirmed">Email Confirmed</option>
                      <option value="documentsUploaded">Documents Uploaded</option>
                      <option value="bankingProvided">Banking Provided</option>
                      <option value="contractCompleted">Contract Completed</option>
                      <option value="payoutExecuted">Payout Executed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Has NOT Completed
                    </label>
                    <select
                      value={campaignFilter.notCompletedMilestone || ""}
                      onChange={(e) => {
                        setCampaignFilter({ ...campaignFilter, notCompletedMilestone: e.target.value || undefined });
                        setPreviewCount(null);
                      }}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Any</option>
                      <option value="documentsUploaded">Documents Uploaded</option>
                      <option value="bankingProvided">Banking Provided</option>
                      <option value="contractCompleted">Contract Completed</option>
                      <option value="payoutExecuted">Payout Executed</option>
                    </select>
                  </div>
                </div>

                {/* Custom Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Custom Message (optional)
                  </label>
                  <textarea
                    value={campaignMessage}
                    onChange={(e) => setCampaignMessage(e.target.value)}
                    rows={3}
                    placeholder="Add a personal message to the invitation email..."
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Preview Count */}
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Recipients: </span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {previewCount !== null ? previewCount : "—"}
                    </span>
                  </div>
                  <button
                    onClick={handlePreviewCount}
                    className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-600 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  >
                    Preview Count
                  </button>
                </div>

                {/* Warning about test mode */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Test Mode:</strong> Invitations will only be sent to @digitaldavid.io email addresses.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCampaignModal(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvitations}
                  disabled={isSendingInvitations || previewCount === null || previewCount === 0}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingInvitations ? "Sending..." : `Send ${previewCount || 0} Invitation(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
